import { useCallback, useState } from 'react';
import dayjs from 'dayjs';

import { CustomAlert, Text } from '../../../shared/uiComponents';
import { InfoRow } from '../../../components/StyledComponents';
import { TEXT } from '../../../shared/Helpers/colors';
import { Dashboard } from '../../../redux/API/ClientAPIHelpers/dashboard';
import { BcbaAuthorizationAllocation, BcbaAuthorizationDetail } from '../../../redux/State/clientSlice/dashboardSlice';

interface AuthorizationDetailPopupProps {
	clientId: string;
	isExpiringSoon?: boolean;
	label: string;
}

const formatHours = (allocation: BcbaAuthorizationAllocation) =>
	allocation.hoursPerWeek ? `${allocation.hoursPerWeek} h/week` : `${allocation.totalHours} h`;

const AuthorizationDetailPopup = ({
	label,
	clientId,
	isExpiringSoon,
}: AuthorizationDetailPopupProps) => {
	const [open, setOpen] = useState<boolean>(false);
	const [detail, setDetail] = useState<BcbaAuthorizationDetail | null>(null);
	const [loading, setLoading] = useState<boolean>(false);

	const handleOpen = useCallback(async () => {
		setOpen(true);
		setLoading(true);
		try {
			const response = await Dashboard.getBcbaClientAuthorizationDetail(clientId);
			if (response && !response.error) {
				setDetail(response);
			}
		} finally {
			setLoading(false);
		}
	}, [clientId]);

	const handleClose = () => {
		setOpen(false);
		setDetail(null);
	};

	return (
		<>
			<span
				className={`btSessionCardMetaItem bcbaAuthLink ${isExpiringSoon ? 'bcbaExpiringSoon' : ''}`}
				onClick={handleOpen}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') handleOpen();
				}}
			>
				{label}
			</span>
			<CustomAlert
				open={open}
				onClose={handleClose}
				title="Authorization details"
				Content={() =>
					loading ? (
						<Text title="Loading..." type="psm" color={TEXT.SECONDARY} />
					) : detail ? (
						<>
							<InfoRow>
								<Text title="Start date" type="psm" fontWeight="semiBold" color={TEXT.TERTIARY} />
								<Text
									title={dayjs(detail.startDate).format('MM/DD/YYYY')}
									type="psm"
									color={TEXT.SECONDARY}
								/>
							</InfoRow>
							<InfoRow>
								<Text title="End date" type="psm" fontWeight="semiBold" color={TEXT.TERTIARY} />
								<Text
									title={dayjs(detail.endDate).format('MM/DD/YYYY')}
									type="psm"
									color={TEXT.SECONDARY}
								/>
							</InfoRow>
							<InfoRow>
								<Text title="Your allocated hours" type="psm" fontWeight="semiBold" color={TEXT.TERTIARY} />
								{!detail.allocations.length && (
									<Text title="None" type="psm" color={TEXT.SECONDARY} />
								)}
							</InfoRow>
							{detail.allocations.map((allocation, index) => (
								<InfoRow key={`${allocation.sessionTypeName}-${index}`}>
									<Text title={allocation.sessionTypeName} type="psm" color={TEXT.TERTIARY} />
									<Text title={formatHours(allocation)} type="psm" color={TEXT.SECONDARY} />
								</InfoRow>
							))}
						</>
					) : (
						<Text title="No active authorization found." type="psm" color={TEXT.SECONDARY} />
					)
				}
			/>
		</>
	);
};

export default AuthorizationDetailPopup;
